import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useNotification } from 'config/Notification';
import {
    fetchECSDimensions as fetchECSDimensionsService,
    fetchECSSubDimensionsByDimensionId as fetchECSSubDimensionsByDimensionIdService,
    saveNewDimension as saveNewDimensionService,
    saveNewSubDimensionByDimension as saveNewSubDimensionByDimensionService,
    updateDimensionById as updateDimensionByIdService,

} from 'services/modules/ecs/data';
import Breadcrumbs from 'config/Breadcrumb';
import {
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    CardHeader,
    FormControlLabel, Radio, RadioGroup, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';



const ConfigureDimensions = () => {

    // Notification
    const { openNotification } = useNotification();

    // State Variables
    const [selectedDimensions, setSelectedDimensions] = useState([]);
    const [subDimension, setSubDimension] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isStep1Editing, setIsStep1Editing] = useState(false)
    const [isStep2Editing, setIsStep2Editing] = useState(false);
    const [editControlId, setEditControlId] = useState(null); 
    const [open, setOpen] = useState(false);
    const [openStep2, setOpenStep2] = useState(false);


    // Fetch dimensions data based on the selected dimension type
    const fetchAllDimensions = async () => {
        try {
            const response = await fetchECSDimensionsService();
            setSelectedDimensions(response.data);
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        }
    };

    // Fetch controls based on the selected dimension
    const fetchSubDimensionsByDimensionId = async (dimensionId) => {
        try {
            const response = await fetchECSSubDimensionsByDimensionIdService({ dimensionId });
            setSubDimension(response.data || []);
        } catch (error) {
            openNotification("Error while fetching controls", 'error');
        }
    };


    useEffect(() => {
        fetchAllDimensions();
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // This method handles to change the pages
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // This method handles to change the rows per page based on user selection
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // MEthod generated the formatted label
    const generateSlug = (label) => {
        return label
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
            .replace(/\s+/g, '-');        // Replace spaces with underscores
    };

    // Validation schemas for each step (Dimension)
    const validationSchemaStep1 = Yup.object({
        label: Yup.string().required('Required'),
    });

    // Formik setup for step 1 Dimension
    const formikStep1 = useFormik({
        initialValues: {
            label: '',
            slug: '',
            description: '',
            weight: 0,
            status: true,
            validationSchema: {}
        },
        validationSchema: validationSchemaStep1,
        onSubmit: async (values) => {
            try {
                let slug = generateSlug(values.label);
                let body = {
                    label: values.label,
                    slug: slug,
                    description: values.description,
                    weight: values.weight,
                    status: values.status,
                    validationSchema: values.validationSchema
                };

                let response;

                if (isStep1Editing) {
                    let dimensionId = values.id;
                    response = await updateDimensionByIdService(dimensionId, { body }); 
                    if (response.status === 200) {
                        openNotification("Dimension updated successfully", 'success');
                        formikStep1.resetForm();

                        fetchAllDimensions();
                        setIsStep1Editing(false);
                    }
                } else {
                    response = await saveNewDimensionService({ body });
                    if (response.status === 201) {
                        openNotification("Dimension saved successfully", 'success');
                        formikStep1.resetForm();
                        fetchAllDimensions();
                        setIsStep1Editing(false);
                    }
                }

            } catch (error) {
                openNotification("Error while saving dimension", 'error');
            }
        },
    });

    // Method handles to edit the dimension details
    const handleStep1Edit = (dimension) => {
        setIsStep1Editing(true);
        formikStep1.setValues({
            id: dimension.id,
            label: dimension.label,
            slug: dimension.slug,
            description: dimension.description,
            weight: dimension.weight,
            status: dimension.status,
            validationSchema: dimension.validationSchema,
        });
        setActiveStep(0); 
    };

    // MEthod handles to change the dimension status
    const handleStatusChange = (event) => {
        const newStatus = event.target.value === 'true';
        if (!newStatus) { // If status is being changed to 'Inactive'
            if (Number(formikStep1.values.weight) === 0) {
                formikStep1.setFieldValue('status', false); 
            } else {
                setOpen(true); 
            }

        } else {
            formikStep1.setFieldValue('status', true); 
        }
    };

    // Method handles to change the status to inactive
    const handleConfirmInactive = () => {
        setOpen(false); 
    };

    // MEthod handles to close the dialog
    const handleClose = () => {
        setOpen(false);
    };

    // MEthod handles to change the status of sub dimension
    const handleStep2StatusChange = (event) => {
        const newStatus = event.target.value === 'true';
        if (!newStatus) {

            if (Number(formikStep2.values.weight) === 0) {
                formikStep2.setFieldValue('status', false); 
            } else {
                setOpenStep2(true); 
            }
        } else {
            formikStep2.setFieldValue('status', true); 
        }
    };

    // MEthod handles to change the status to inactive for sub dimension
    const handleStep2ConfirmInactive = () => {
        setOpenStep2(false); 
    };

    // MEthod handles to close the dialog for sub dimension
    const handleStep2Close = () => {
        setOpenStep2(false);
    };

    // Validation schema for sub dimension
    const validationSchemaStep2 = Yup.object({
        dimension: Yup.string().required('Required'),
        label: Yup.string().required('Required'),
    });

    // Formik setup for step 2 sub dimension
    const formikStep2 = useFormik({
        initialValues: {
            dimension: '',
            label: '',
            slug: '',
            description: '',
            weight: 0,
            status: true,
            validationSchema: {}
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            try {
                let slug = generateSlug(values.label);
                let body = {
                    label: values.label,
                    slug: slug,
                    description: values.description,
                    weight: values.weight,
                    status: values.status,
                    validationSchema: values.validationSchema
                };

                if (isStep2Editing) {
                    let dimension = editControlId;
                    const response = await updateDimensionByIdService(dimension, { body });
                    if (response.status === 200) {
                        openNotification("Sub-Dimension updated successfully", 'success');
                        fetchSubDimensionsByDimensionId(values.dimension);
                        formikStep2.resetForm();
                        formikStep2.setFieldValue('dimension', values.dimension);
                        setIsStep2Editing(false); 
                        setEditControlId(null); 
                    }
                } else {
                    const response = await saveNewSubDimensionByDimensionService({ dimension: values.dimension, body });
                    if (response.status === 201) {
                        openNotification("Sub-Dimension saved successfully", 'success');
                        fetchSubDimensionsByDimensionId(values.dimension);
                        formikStep2.resetForm();
                        formikStep2.setFieldValue('dimension', values.dimension);
                    }
                }
            } catch (error) {
                openNotification("Error while saving control", 'error');
            }
        }

    });

    // MEthod handles to edit the sub dimension details
    const handleStep2EditClick = (subdimension) => {
        setIsStep2Editing(true);
        setEditControlId(subdimension.id); 
        // Update only the specific fields in the Formik form
        formikStep2.setFieldValue('label', subdimension.label);
        formikStep2.setFieldValue('slug', subdimension.slug);
        formikStep2.setFieldValue('description', subdimension.description);
        formikStep2.setFieldValue('weight', subdimension.weight);
        formikStep2.setFieldValue('validationSchema', subdimension.validationSchema);
        formikStep2.setFieldValue('status', subdimension.status);
        formikStep2.setFieldValue('validationSchema', subdimension.validationSchema);
    };

    useEffect(() => {
        if (formikStep2.values.dimension) {
            fetchSubDimensionsByDimensionId(formikStep2.values.dimension);
        }
         // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikStep2.values.dimension]);

    // Steps for the stepper
    const steps = ['Add Dimension', 'Add Sub-Dimension'];

    return (
        <div className='main-cont'>
            <Card>
                <CardHeader
                    title={
                        <React.Fragment>
                            <div className="card-head">
                                <div className="flex-head">
                                    <label className="label">
                                        <Breadcrumbs paths={['ECS Configurator', 'Configure Dimensions']} />
                                    </label>
                                </div>
                                <div>
                                    <Button
                                        variant="contained"
                                        className="back font"
                                        onClick={() => window.history.back()}
                                    >
                                        Back
                                    </Button>
                                </div>
                            </div>
                        </React.Fragment>
                    }
                />
                <CardContent>
                    <Divider className='my-1'  />
                    {(
                        <>
                            <Stepper activeStep={activeStep} alternativeLabel>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Divider className='my-1'  />
                            <CardContent className='loader-container '>
                                {activeStep === 0 && (
                                    <div className='d-flex justify-content-center mb-4'>
                                        <Card className="row company-screen-bg col-md-10">
                                            <CardContent>
                                                <form onSubmit={formikStep1.handleSubmit}>
                                                    <div className='row'>
                                                        <FormControl className='col-md-6 px-2' margin="normal">
                                                            <TextField
                                                                label="Name"
                                                                name="label"
                                                                onChange={formikStep1.handleChange}
                                                                value={formikStep1.values.label}
                                                                error={formikStep1.touched.label && Boolean(formikStep1.errors.label)}
                                                                helperText={formikStep1.touched.label && formikStep1.errors.label}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <div className='row'>
                                                        <div className='col-md-12 px-2'>
                                                            <FormControl fullWidth margin="normal">
                                                                <TextField
                                                                    label="Description"
                                                                    name="description"
                                                                    onChange={formikStep1.handleChange}
                                                                    value={formikStep1.values.description}
                                                                    error={formikStep1.touched.description && Boolean(formikStep1.errors.description)}
                                                                    helperText={formikStep1.touched.description && formikStep1.errors.description}
                                                                />
                                                            </FormControl>
                                                        </div>
                                                        {isStep1Editing && (
                                                            <>
                                                                <div className='row col-md-12 px-2 align-items-center'>
                                                                    <FormControl component="fieldset" className='d-flex' margin="normal">
                                                                        <RadioGroup
                                                                            row
                                                                            aria-label="status"
                                                                            name="status"
                                                                            value={formikStep1.values.status ? 'true' : 'false'}
                                                                            onChange={handleStatusChange}
                                                                        >
                                                                            <FormControlLabel value="true" control={<Radio />} label="Active" />
                                                                            <FormControlLabel value="false" control={<Radio />} label="Inactive" />
                                                                        </RadioGroup>
                                                                    </FormControl>
                                                                </div>
                                                            </>
                                                        )}


                                                    </div>

                                                    <div className='d-flex justify-content-center align-items-center'>

                                                        <Button color="primary" variant="contained" type="submit">
                                                            {isStep1Editing ? 'Update' : 'Save'}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            className="danger mx-3"
                                                            onClick={() => {
                                                                formikStep1.resetForm();
                                                                setIsStep1Editing(false); 
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => setActiveStep((prev) => prev + 1)}
                                                        >
                                                            Next
                                                        </Button>

                                                    </div>
                                                    <Dialog
                                                        open={open}
                                                        onClose={handleClose}
                                                        aria-labelledby="alert-dialog-title"
                                                        aria-describedby="alert-dialog-description"
                                                    >
                                                        <DialogTitle id="alert-dialog-title">{"Confirm Inactive Status"}</DialogTitle>
                                                        <DialogContent>
                                                            <DialogContentText id="alert-dialog-description">
                                                                Would you really like to change the status to "Inactive"? the weightage should be zero.
                                                            </DialogContentText>
                                                        </DialogContent>
                                                        <DialogActions>
                                                            <Button onClick={handleConfirmInactive} color="secondary" autoFocus>
                                                                Ok
                                                            </Button>
                                                        </DialogActions>
                                                    </Dialog>
                                                </form>
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}
                                {activeStep === 0 && selectedDimensions && selectedDimensions.length > 0 && (
                                    <div className='mb-4'>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Label</TableCell>
                                                    <TableCell>Weightage</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedDimensions && selectedDimensions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((dimension, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{dimension.label}</TableCell>
                                                        <TableCell>{dimension.weight}</TableCell>
                                                        <TableCell className='wordWrap'>
                                                            {dimension.description}
                                                        </TableCell>
                                                        <TableCell>{dimension.status ? 'Active' : 'Inactive'}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={() => handleStep1Edit(dimension)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 100]}
                                            component="div"
                                            count={selectedDimensions.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                        />

                                    </div>
                                )}

                                {activeStep === 1 && (
                                    <div className='d-flex justify-content-center mb-4'>
                                        <Card className="row company-screen-bg col-md-10">
                                            <CardContent>
                                                <form onSubmit={formikStep2.handleSubmit}>
                                                    <div className="row ">
                                                        <FormControl className='col-md-6 px-2' margin="normal">
                                                            <InputLabel>Dimension</InputLabel>
                                                            <Select
                                                                name="dimension"
                                                                value={formikStep2.values.dimension}
                                                                onChange={formikStep2.handleChange}
                                                                error={formikStep2.touched.dimension && Boolean(formikStep2.errors.dimension)}
                                                            >
                                                                {selectedDimensions && selectedDimensions
                                                                    .filter(dimension => dimension.status === true) 
                                                                    .map((dimension) => (
                                                                        <MenuItem key={dimension.id} value={dimension.id}>
                                                                            {dimension.label}
                                                                        </MenuItem>
                                                                    ))
                                                                }
                                                            </Select>
                                                            {formikStep2.touched.dimension && formikStep2.errors.dimension && (
                                                                <div className="error-message">{formikStep2.errors.dimension}</div>
                                                            )}
                                                        </FormControl>
                                                        <FormControl className='col-md-6 px-2' margin="normal">
                                                            <TextField
                                                                label="Name"
                                                                name="label"
                                                                onChange={formikStep2.handleChange}
                                                                value={formikStep2.values.label}
                                                                error={formikStep2.touched.label && Boolean(formikStep2.errors.label)}
                                                                helperText={formikStep2.touched.label && formikStep2.errors.label}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                    <FormControl fullWidth margin="normal">
                                                        <TextField
                                                            label="Description"
                                                            name="description"
                                                            onChange={formikStep2.handleChange}
                                                            value={formikStep2.values.description}
                                                            error={formikStep2.touched.description && Boolean(formikStep2.errors.description)}
                                                            helperText={formikStep2.touched.description && formikStep2.errors.description}
                                                        />
                                                    </FormControl>
                                                    {isStep2Editing && (
                                                        <>
                                                            <div className='row col-md-12 px-2 align-items-center' >
                                                                <FormControl component="fieldset" className='d-flex' margin="normal">
                                                                    <RadioGroup
                                                                        row
                                                                        aria-label="status"
                                                                        name="status"
                                                                        value={formikStep2.values.status ? 'true' : 'false'}
                                                                        onChange={handleStep2StatusChange}
                                                                    >
                                                                        <FormControlLabel value="true" control={<Radio />} label="Active" />
                                                                        <FormControlLabel value="false" control={<Radio />} label="Inactive" />
                                                                    </RadioGroup>
                                                                </FormControl>
                                                            </div>
                                                        </>
                                                    )}


                                                    <div className='d-flex justify-content-center align-items-center' >
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => setActiveStep((prev) => prev - 1)}
                                                        >
                                                            Back
                                                        </Button>
                                                        <Button color="primary"
                                                            className='mx-3'
                                                            variant="contained" type="submit">
                                                            {isStep2Editing ? 'Update' : 'Save'}
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            className="danger"
                                                            onClick={() => {
                                                                let dimension = formikStep2.values.dimension;
                                                                formikStep2.resetForm();
                                                                formikStep2.setFieldValue('dimension', dimension);
                                                                setIsStep2Editing(false); 
                                                                setEditControlId(null);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>

                                                    </div>
                                                    <Dialog
                                                        open={openStep2}
                                                        onClose={handleStep2Close}
                                                        aria-labelledby="alert-dialog-title"
                                                        aria-describedby="alert-dialog-description"
                                                    >
                                                        <DialogTitle id="alert-dialog-title">{"Confirm Inactive Status"}</DialogTitle>
                                                        <DialogContent>
                                                            <DialogContentText id="alert-dialog-description">
                                                                Would you really like to change the status to "Inactive"? the weightage should be zero.
                                                            </DialogContentText>
                                                        </DialogContent>
                                                        <DialogActions>
                                                            <Button onClick={handleStep2ConfirmInactive} color="secondary" autoFocus>
                                                                Ok
                                                            </Button>
                                                        </DialogActions>
                                                    </Dialog>
                                                </form>
                                            </CardContent>
                                        </Card>
                                    </div>
                                )}
                                {activeStep === 1 && subDimension.length > 0 && (
                                    <div className='mb-4'>

                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Label</TableCell>
                                                    <TableCell>Weightage</TableCell>
                                                    <TableCell>Description</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {subDimension.map((dimension, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{dimension.label}</TableCell>
                                                        <TableCell>{dimension.weight}</TableCell>
                                                        <TableCell className='wordWrap'>
                                                            {dimension.description}
                                                        </TableCell>
                                                        <TableCell>{dimension.status ? 'Active' : 'Inactive'}</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="contained"
                                                                color="secondary"
                                                                onClick={() => handleStep2EditClick(dimension)}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 100]}
                                            component="div"
                                            count={subDimension.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                        />
                                    </div>
                                )}

                                <Divider className='my-1'  />
                            </CardContent>

                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
export default ConfigureDimensions;
